import React, { useRef, useEffect, useMemo, useState } from 'react';
import mapboxgl from '!mapbox-gl';
import { toast } from 'react-toastify';
import * as actionTypes from "@redux/actionTypes";
import { useDispatch, useSelector } from 'react-redux';
import InProgressBot from '@images/dashboard/inProgressBot.png';
import OnlineBot from '@images/dashboard/onlineBot.png';
import OfflineBot from '@images/dashboard/offlineBot.png';
import VerifiedBot from '@images/dashboard/verifiedBot.png';
import allHex from '@images/map-clusters.png';
import { getDistanceFromLatLonInKm, isLatLogValid } from '@util/config';
import { useAuth } from '@context';
import toastErrorMessages from '@util/toastErrorMessage';
import { MAPBOX_ACCESS_TOKEN, SOURCES } from '@constants/map'
import { addLayer, addSource, formatGeoJson, removeLayers, updateSource } from '@utils/maps';
import { safeLocalStorage } from "@utils/localstorage";
import { imageSrc } from '@utils/imageSrc';
import { useRouter } from 'next/router';

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const ITEM_STATUS = {
  mission_in_progress: 'mission_in_progress',
  mission_ready: 'mission_ready',
  offline: 'offline',
}

const DRIVE_MODES = {
  mission: 'mission'
}

const BOUNDS = [
  [-180, -90], // Southwest coordinates
  [180, 90]    // Northeast coordinates
];

const loadImage = ({ map, src, name }) =>
  new Promise((resolve, reject) => {
    map.loadImage(imageSrc(src), (error, image) => {
      if (error) return reject(error);
      if (!map.hasImage(name)) map.addImage(name, image);
      resolve();
    });
  });

// Resolves only once every icon is registered via addImage, so layers added
// afterwards find their icon-image immediately instead of rendering the bare
// cluster count until the PNG finishes decoding.
const loadImages = ({ map }) =>
  Promise.all([
    loadImage({ map, src: InProgressBot, name: 'inProgress' }),
    loadImage({ map, src: OnlineBot, name: 'online' }),
    loadImage({ map, src: OfflineBot, name: 'offline' }),
    loadImage({ map, src: VerifiedBot, name: 'verified' }),
    loadImage({ map, src: allHex, name: 'allHex' })
  ]);

// TODO: move to utils
const isDriveModeMission = (driveMode) => driveMode === DRIVE_MODES.mission
const isMissionInProgress = (status) => status === ITEM_STATUS.mission_in_progress
const isMissionReady = (status) => status === ITEM_STATUS.mission_ready
const isOffline = (status) => status === ITEM_STATUS.offline

const RobotTrack = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const mapContainerRef = useRef(null);
  const { setNoBotInfoPopUp } = useAuth();
  const token = safeLocalStorage.getItem('token')
  const { statusDetails, allBot } = useSelector((state) => state.botDetail);
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [map, setMap] = useState(null)

  const loadMap = ({ robots, token, statusDetails }) => {
    let dataMap = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      zoom: 1,
      projection: 'mercator',
      maxBounds: BOUNDS
    });

    dataMap.setMaxZoom(18);
    dataMap.scrollZoom.enable({ around: 'center' });

    const filteredRobots = filterRobots({ bots: robots, token, filters: statusDetails })
    const geoJsonRobot = formatGeoJson({ data: filteredRobots, source: SOURCES.earthquakes })

    setMap(dataMap)

    dataMap.on('load', async () => {
      removeLayers({ map: dataMap });
      await loadImages({ map: dataMap });
      addSource({ map: dataMap, source: SOURCES.earthquakes, data: geoJsonRobot });
      addLayer({ map: dataMap, source: SOURCES.earthquakes, kyc: (token?.user?.kyc_id || token?.user?.has_test_drive) })

      // inspect a cluster on click
      dataMap.on('click', 'clusters', (e) => {
        const features = dataMap.queryRenderedFeatures(e.point, {
          layers: ['clusters']
        });
        const clusterId = features.length && features[0].properties.cluster_id;
        dataMap.getSource(SOURCES.earthquakes).getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;

          dataMap.easeTo({
            center: features.length && features[0].geometry.coordinates,
            zoom: zoom
          });
        });
      });

      dataMap.on('click', 'unclustered-point', (e) => {
        const botPointData = e.features[0].properties;
        gammaHandler(JSON.parse(botPointData.point));
      });

      dataMap.on('mouseenter', 'clusters', () => {
        dataMap.getCanvas().style.cursor = 'pointer';
      });
      dataMap.on('mouseleave', 'clusters', () => {
        dataMap.getCanvas().style.cursor = '';
      });

      setIsMapLoaded(true)
    });
  }

  const filterRobots = ({ bots, token, filters }) => {
    if (!bots || Object.keys(bots).length === 0) return []

    let filteredStatus;
    const { checked, default: defaultStatus } = filters;

    try {
      if (token?.user?.kyc_id || token?.user?.has_test_drive) {
        filteredStatus = bots.filter((item) => {
          const { status, drive_mode: driveMode } = item;

          if (checked.mission_in_progress && isMissionInProgress(status) && isDriveModeMission(driveMode)) return true
          if (checked.mission_ready && isMissionReady(status) && isDriveModeMission(driveMode)) return true
          if (checked.offline && isOffline(status) && isDriveModeMission(driveMode)) return true
          if (defaultStatus && isDriveModeMission(driveMode)) return true

          return false;
        });
      } else {
        filteredStatus = bots.filter((item) => {
          const { status, drive_mode: driveMode, kyc_required: kycRequired } = item;

          if (checked.mission_in_progress && isMissionInProgress(status) && isDriveModeMission(driveMode) && !kycRequired) return true
          if (checked.mission_ready && isMissionReady(status) && isDriveModeMission(driveMode) && !kycRequired) return true
          if (checked.offline && isOffline(status) && isDriveModeMission(driveMode) && !kycRequired) return true
          if (checked.verified && isMissionReady(status) && isDriveModeMission(driveMode) && kycRequired) return true
          if (defaultStatus && isDriveModeMission(driveMode) && !kycRequired) return true

          return false;
        });
      }

      if (filteredStatus) {
        const filterdata = filteredStatus?.filter((x) =>
          Boolean(
            x.longitude &&
            x.latitude &&
            x.status !== 'test_ready' &&
            x.status !== 'unavailable' &&
            isLatLogValid(x.latitude, x.longitude)
          )
        );
        if (filterdata?.length <= 0) {
          setNoBotInfoPopUp(true);
        } else {
          setNoBotInfoPopUp(false);
        }

        highestDistanceHandler(filterdata)

        return filterdata
      } else {
        toast.error(toastErrorMessages.botDetailsNotAvailable, { position: 'top-right' });
      }
    } catch (error) {
      console.log('error: ', error);
    }
  }

  const highestDistanceHandler = (filterdata) => {
    if (filterdata.length > 1) {
      // Create an array of promises for each pair of points
      const distancePromises = filterdata.map((point, index) =>
        Promise.all(
          filterdata.slice(index + 1).map(
            (pnt) =>
              new Promise((resolve) => {
                const longDistance = getDistanceFromLatLonInKm(point, pnt);
                resolve({
                  distance: longDistance,
                  point,
                  pnt
                });
              })
          )
        )
      );

      Promise.all(distancePromises.flat()).then((allData) => {
        // allData is an array containing all the resolved values
        const highestDistance = allData[0].reduce((acc, curr) => (acc.distance > curr.distance ? acc : curr));
        if (highestDistance && highestDistance.point && highestDistance.pnt) {
          fitBounds(highestDistance.point, highestDistance.pnt);
        }
      });
    }
  };

  const fitBounds = (firstPoint, secoundPoint) => {
    const point1 = [firstPoint.longitude, firstPoint.latitude];
    const point2 = [secoundPoint.longitude, secoundPoint.latitude];

    const centerPoints = [
      (point1[0] + point2[0]) / 2,
      (point1[1] + point2[1]) / 2
    ];

    if (map) {
      map.flyTo({
        center: centerPoints,
        zoom: 1.5,
        speed: 1,
        curve: 1,
        essential: true
      });
    }

  };

  const gammaHandler = async (botData) => {
    const missionBotId = botData.id;
    if (missionBotId) {
      dispatch({
        type: actionTypes.BOT_DETAILS,
        payload: botData,
      });
      window.location.replace(`/bot/${missionBotId}`)
      // TODO: @franciscomasi To use Navigator we need to clean Redux properly
      // navigate(`/bot/${missionBotId}`);
    } else {
      toast.error(toastErrorMessages.missionNotFound, { position: "top-right" });
    }
  };

  useEffect(() => {
    if (isMapLoaded) return

    if (allBot?.length > 0) {
      loadMap({ robots: allBot, token, statusDetails })
    }

    return () => {
      if (map) map.remove();
    }
  }, [isMapLoaded, allBot])

  useEffect(() => {
    if (!isMapLoaded || !map) return

    if (allBot?.length > 0) {
      const filteredRobots = filterRobots({ bots: allBot, token, filters: statusDetails })
      const geoJsonRobot = formatGeoJson({ data: filteredRobots, source: SOURCES.earthquakes })

      updateSource({ map, sourceName: SOURCES.earthquakes, geoJSON: geoJsonRobot });
    }
  }, [statusDetails, allBot, isMapLoaded, map]);

  return (
    <div
      className="mapContainerRef"
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      }}
      ref={mapContainerRef}
    ></div>
  );
};

export default RobotTrack;
